import React, { useState, useEffect } from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native'
import { Text, Input, Button, Spinner, Datepicker, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { format, parse } from 'date-fns'
import { useToast } from 'react-native-toast-notifications'
import { useAtomValue, getDefaultStore } from 'jotai'
import { dbAtom, storesDictAtom, organizationIdAtom } from '../../global/AtomsProvider'
import { getLocalStoreDate, getDailyStatID } from '../../utils/timezoneFunctions'
import HeaderIconButton from '../../components/HeaderIconButton'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { snapshotExists } from '../../global/Firebase/types'
import { reportFirestoreOnlyWriterUsage } from '../../global/analytics/firestoreOnlyWriterUsage'

export default function EditDailyStatsScreen({ navigation, route }) {
  const { t } = useTranslation()
  const toast = useToast()
  const theme = useTheme()
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const storesDict = jotaiStore.get(storesDictAtom)
  const organizationId = jotaiStore.get(organizationIdAtom)
  
  const [selectedDate, setSelectedDate] = useState(route.params?.date || new Date())
  const [selectedStore, setSelectedStore] = useState(route.params?.store || 'all')
  const [dailyStat, setDailyStat] = useState(null)
  const [originalDailyStat, setOriginalDailyStat] = useState(null)
  const [editedFields, setEditedFields] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          iconName='save-outline'
          onPress={handleSave}
          disabled={isSaving || Object.keys(editedFields).length === 0}
        />
      ),
      headerLeft: () => (
        <HeaderIconButton
          iconName='arrow-back-outline'
          onPress={() => navigation.goBack()}
        />
      ),
    })
  }, [navigation, editedFields, isSaving])

  useEffect(() => {
    if (selectedDate && selectedStore && selectedStore !== 'all' && db) {
      loadDailyStat()
    }
  }, [selectedDate, selectedStore, db])

  const loadDailyStat = async () => {
    if (!selectedDate || selectedStore === 'all' || !db) {
      return
    }

    try {
      setIsLoading(true)
      
      const localDate = getLocalStoreDate(selectedStore, selectedDate)
      const dailyStatId = getDailyStatID(selectedStore, selectedDate)
      
      const docSnap = await db.collection('dailyStats').doc(dailyStatId).get()
      
      if (snapshotExists(docSnap)) {
        const data = docSnap.data()
        const statData = {
          id: docSnap.id,
          ref: docSnap.ref,
          ...data
        }
        
        // Convert Firestore Timestamps to Date objects for display
        if (statData.date && statData.date.toDate) {
          statData.date = statData.date.toDate()
        }
        
        setDailyStat(statData)
        setOriginalDailyStat(JSON.parse(JSON.stringify(statData))) // Deep copy
        setEditedFields({})
      } else {
        toast.show('Daily stat не найден для выбранной даты и магазина', {type: 'warning'})
        setDailyStat(null)
        setOriginalDailyStat(null)
        setEditedFields({})
      }
    } catch (error) {
      console.error('Error loading daily stat:', error)
      toast.show('Ошибка при загрузке daily stat', {type: 'error'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldChange = (fieldName, value, fieldType) => {
    if (!dailyStat) return

    let processedValue = value
    // Convert value based on type
    if (fieldType === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value) || 0
    } else if (fieldType === 'boolean') {
      processedValue = value === 'true' || value === true
    } else if (fieldType === 'date') {
      processedValue = value instanceof Date ? value : new Date(value)
    }

    // Check if value changed from original
    const originalValue = originalDailyStat[fieldName]
    const hasChanged = originalValue !== processedValue
    if (hasChanged) {
      setEditedFields(prev => ({
        ...prev,
        [fieldName]: processedValue
      }))
    } else {
      setEditedFields(prev => {
        const newFields = { ...prev }
        delete newFields[fieldName]
        return newFields
      })
    }

    setDailyStat(prev => ({
      ...prev,
      [fieldName]: processedValue
    }))
  }

  const handleSave = async () => {
    if (!dailyStat || Object.keys(editedFields).length === 0) {
      return
    }

    try {
      setIsSaving(true)

      // Prepare update object with only changed fields
      const updateData = {}
      for (const [key, value] of Object.entries(editedFields)) {
        updateData[key] = value
      }

      reportFirestoreOnlyWriterUsage('editDailyStatManually', organizationId)
      await dailyStat.ref.update(updateData)

      toast.show('Daily stat успешно обновлен', {type: 'success'})
      
      // Reload to get updated data
      await loadDailyStat()
    } catch (error) {
      console.error('Error saving daily stat:', error)
      toast.show('Ошибка при сохранении daily stat', {type: 'error'})
    } finally {
      setIsSaving(false)
    }
  }

  const getFieldType = (value) => {
    if (value === null || value === undefined) return 'null'
    if (typeof value === 'number') return 'number'
    if (typeof value === 'boolean') return 'boolean'
    if (value instanceof Date) return 'date'
    if (typeof value === 'object') return 'object'
    return 'string'
  }

  const formatValue = (value, type) => {
    if (value === null || value === undefined) return ''
    if (type === 'date' && value instanceof Date) {
      return format(value, 'yyyy-MM-dd HH:mm:ss')
    }
    if (type === 'boolean') {
      return value ? 'true' : 'false'
    }
    if (type === 'object') {
      return JSON.stringify(value)
    }
    return value.toString()
  }

  const renderField = (fieldName, value) => {
    if (['id', 'ref', 'organizationId'].includes(fieldName)) {
      return null // Skip metadata fields
    }

    const type = getFieldType(value)
    const isEdited = editedFields.hasOwnProperty(fieldName)
    const displayValue = formatValue(value, type)

    return (
      <View key={fieldName} style={[styles.fieldContainer, { backgroundColor: theme['background-basic-color-1'], borderColor: theme['border-basic-color-3'] }, isEdited && styles.fieldContainerEdited]}>
        <Text category='label' style={styles.fieldLabel}>
          {fieldName}
          {isEdited && <Text style={styles.editedIndicator}> *</Text>}
        </Text>
        <Text category='c1' style={[styles.fieldType, { color: theme['text-hint-color'] }]}>
          Type: {type}
        </Text>
        {type === 'object' ? (
          <Text style={[styles.readOnlyValue, { backgroundColor: theme['background-basic-color-2'] }]} selectable>
            {displayValue}
          </Text>
        ) : type === 'boolean' ? (
          <View style={styles.booleanContainer}>
            <Button
              size='small'
              appearance={value ? 'filled' : 'outline'}
              onPress={() => handleFieldChange(fieldName, !value, type)}
              style={styles.booleanButton}
            >
              {value ? 'True' : 'False'}
            </Button>
          </View>
        ) : (
          <Input
            value={displayValue}
            onChangeText={(text) => {
              if (type === 'number') {
                // Allow only numbers and decimal point
                const numericValue = text.replace(/[^0-9.-]/g, '')
                handleFieldChange(fieldName, numericValue, type)
              } else {
                handleFieldChange(fieldName, text, type)
              }
            }}
            keyboardType={type === 'number' ? 'decimal-pad' : 'default'}
            style={styles.input}
            status={isEdited ? 'warning' : 'basic'}
          />
        )}
      </View>
    )
  }

  if (!db) {
    return (
      <View style={styles.container}>
        <Text>Database not available</Text>
      </View>
    )
  }

  return (
    <KeyboardAvoidingView 
      style={{flex: 1}} 
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={100}
    >
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled'>
        <View style={[styles.filtersContainer, { borderBottomColor: theme['border-basic-color-3'] }]}>
          <Text category='h6' style={styles.sectionTitle}>Выбор даты и магазина</Text>
          
          <Datepicker
            label='Дата'
            date={selectedDate}
            onSelect={(date) => setSelectedDate(date)}
            min={new Date('2020-01-01')}
            max={new Date()}
            style={styles.datePicker}
          />
          
          <StoreChooserHorizontal
            store={selectedStore}
            style={styles.storeChooser}
            showAll={false}
            onChange={(newStore) => {
              setSelectedStore(newStore)
            }}
          />
          
          <Button
            onPress={loadDailyStat}
            disabled={isLoading || selectedStore === 'all'}
            style={styles.loadButton}
          >
            {isLoading ? 'Загрузка...' : 'Загрузить'}
          </Button>
        </View>

        {isLoading && (
          <View style={styles.spinnerContainer}>
            <Spinner />
          </View>
        )}

        {!isLoading && dailyStat && (
          <>
            <View style={[styles.infoContainer, { backgroundColor: theme['background-basic-color-2'] }]}>
              <Text category='h6' style={styles.sectionTitle}>Информация</Text>
              <Text style={styles.infoText}>
                ID: {dailyStat.id}
              </Text>
              <Text style={styles.infoText}>
                Дата: {dailyStat.date ? format(dailyStat.date, 'yyyy-MM-dd HH:mm:ss') : 'N/A'}
              </Text>
              <Text style={styles.infoText}>
                Магазин: {storesDict[selectedStore]?.name || selectedStore}
              </Text>
              {Object.keys(editedFields).length > 0 && (
                <Text style={styles.changesInfo}>
                  Изменено полей: {Object.keys(editedFields).length}
                </Text>
              )}
            </View>

            <View style={styles.fieldsContainer}>
              <Text category='h6' style={styles.sectionTitle}>Поля</Text>
              
              {Object.keys(dailyStat)
                .filter(key => !['id', 'ref', 'organizationId'].includes(key))
                .sort()
                .map(key => renderField(key, dailyStat[key]))}
            </View>

            <Button
              onPress={handleSave}
              disabled={isSaving || Object.keys(editedFields).length === 0}
              style={styles.saveButton}
              appearance='filled'
            >
              {isSaving ? 'Сохранение...' : `Сохранить изменения (${Object.keys(editedFields).length})`}
            </Button>
          </>
        )}

        {!isLoading && !dailyStat && selectedDate && selectedStore !== 'all' && (
          <Text style={[styles.noDataText, { color: theme['text-hint-color'] }]}>
            Daily stat не найден для выбранной даты и магазина
          </Text>
        )}

        <View style={{height: 50}} />
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  filtersContainer: {
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
  },
  sectionTitle: {
    marginTop: 20,
    marginBottom: 10,
  },
  datePicker: {
    marginVertical: 10,
  },
  storeChooser: {
    marginVertical: 10,
  },
  loadButton: {
    marginTop: 10,
  },
  spinnerContainer: {
    alignItems: 'center',
    padding: 40,
  },
  infoContainer: {
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
  },
  infoText: {
    marginVertical: 5,
    fontSize: 14,
  },
  changesInfo: {
    marginTop: 10,
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ff9800',
  },
  fieldsContainer: {
    marginBottom: 20,
  },
  fieldContainer: {
    marginBottom: 20,
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
  },
  fieldContainerEdited: {
    borderColor: '#ff9800',
    borderWidth: 2,
    backgroundColor: '#fff8e1',
  },
  fieldLabel: {
    marginBottom: 5,
    fontWeight: 'bold',
  },
  editedIndicator: {
    color: '#ff9800',
    fontWeight: 'bold',
  },
  fieldType: {
    marginBottom: 10,
    fontStyle: 'italic',
  },
  input: {
    marginTop: 5,
  },
  readOnlyValue: {
    marginTop: 5,
    padding: 10,
    borderRadius: 4,
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  booleanContainer: {
    marginTop: 5,
  },
  booleanButton: {
    alignSelf: 'flex-start',
  },
  saveButton: {
    marginTop: 20,
    marginBottom: 20,
  },
  noDataText: {
    textAlign: 'center',
    marginTop: 40,
    fontSize: 16,
  },
})

